import * as React from 'react';
import BPromise from 'bluebird';
import moment from 'moment';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ActionItem } from 'Src/components/ActionsButton/ActionsButton';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import {
  getAllSuperAdminUsers,
  getSuperAdminUser,
  getSuperAdminUsers,
  getSuperAdminUsersCount,
  SuperAdminUser
} from 'Src/helpers/service/superadmin/users';
import './SuperAdminUsers.css';

function formatDate(value?: string): string {
  if (!value) {
    return '';
  }

  const date = moment(value);
  return date.isValid() ? date.format('DD-MM-YYYY') : '';
}

function formatDateTime(value?: string): string {
  if (!value) {
    return '';
  }

  const date = moment(value);
  return date.isValid() ? date.format('DD-MM-YYYY HH:mm') : '';
}

function getUserName(user: SuperAdminUser): string {
  return [user.firstName, user.lastName].filter(Boolean).join(' ');
}

function getUserGender(user: SuperAdminUser): string {
  switch (user.gender) {
    case 'MALE':
      return 'Male';
    case 'FEMALE':
      return 'Female';
    default:
      return user.gender || '';
  }
}

function getUserStatus(user: SuperAdminUser): string {
  switch (user.status) {
    case 'ACTIVE':
      return 'Active';
    case 'BLOCKED':
      return 'Inactive';
    case 'REMOVED':
      return 'Removed';
    case 'WIPED':
      return 'Wiped';
    default:
      return user.status || '';
  }
}

const USER_GRID_OPTIONS = {
  loggedIn: [
    { text: 'Yes', value: 'YES' },
    { text: 'No', value: 'NO' }
  ],
  gender: [
    { text: 'Male', value: 'MALE' },
    { text: 'Female', value: 'FEMALE' }
  ],
  status: [
    { text: 'Active', value: 'ACTIVE' },
    { text: 'Inactive', value: 'BLOCKED' },
    { text: 'Removed', value: 'REMOVED' },
    { text: 'Wiped', value: 'WIPED' }
  ],
  permissionPreset: [
    { text: 'Admin', value: 'ADMIN' },
    { text: 'Manager', value: 'MANAGER' },
    { text: 'Teacher', value: 'TEACHER' },
    { text: 'Coach', value: 'COACH' },
    { text: 'Student', value: 'STUDENT' },
    { text: 'Parent', value: 'PARENT' },
    { text: 'Governor', value: 'GOVERNOR' },
    { text: 'Public blogger', value: 'PUBLIC_BLOGGER' },
    { text: 'Tournament follower', value: 'TOURNAMENT_FOLLOWER' }
  ]
};

const USER_EXACT_FILTER_FIELDS = ['gender', 'status', 'loggedIn'];

function getSuperAdminUsersWhere(filters: any): any {
  const filtersForGenericWhere = { ...filters };

  USER_EXACT_FILTER_FIELDS.forEach(field => {
    delete filtersForGenericWhere[field];
  });

  delete filtersForGenericWhere['permissionPreset'];
  delete filtersForGenericWhere['email'];
  delete filtersForGenericWhere['phone'];

  const where = removeEmptyAnd(getServerFieldSectionWhere(filtersForGenericWhere));
  const emailSearch = typeof filters.email === 'string' ? filters.email.trim() : '';
  const phoneSearch = typeof filters.phone === 'string' ? filters.phone.trim() : '';

  if (emailSearch) {
    where.emailSearch = { like: emailSearch, options: 'i' };
  }

  if (phoneSearch) {
    where.phoneSearch = { like: phoneSearch, options: 'i' };
  }

  const gender = filters.gender;

  if (Array.isArray(gender) && gender.length > 0) {
    where['gender'] = { $in: gender };
  } else if (typeof gender === 'string' && gender !== '') {
    where['gender'] = gender;
  }

  const status = filters.status;

  if (Array.isArray(status) && status.length > 0) {
    where['status'] = { $in: status };
  } else if (typeof status === 'string' && status !== '') {
    where['status'] = status;
  }

  if (
    filters.loggedIn === 'YES' ||
    (Array.isArray(filters.loggedIn) && filters.loggedIn.length === 1 && filters.loggedIn[0] === 'YES')
  ) {
    where.lastLoginAt = { $exists: true };
  } else if (
    filters.loggedIn === 'NO' ||
    (Array.isArray(filters.loggedIn) && filters.loggedIn.length === 1 && filters.loggedIn[0] === 'NO')
  ) {
    where.lastLoginAt = { $exists: false };
  }

  const preset = filters['permissionPreset'];
  if (Array.isArray(preset) && preset.length > 0) {
    where['permissions'] = {
      $elemMatch: { preset: { $in: preset }, status: 'ACTIVE' }
    };
  } else if (typeof preset === 'string' && preset !== '') {
    where['permissions'] = {
      $elemMatch: { preset: preset, status: 'ACTIVE' }
    };
  }

  return removeEmptyAnd(where);
}

function getUserBirthday(user: SuperAdminUser): string {
  return formatDate(user.birthday);
}

function getUserCreatedAt(user: SuperAdminUser): string {
  return formatDateTime(user.createdAt);
}

function getUserUpdatedAt(user: SuperAdminUser): string {
  return formatDateTime(user.updatedAt);
}

function getUserLoggedIn(user: SuperAdminUser): string {
  return user.lastLoginAt ? 'Yes' : 'No';
}

function getUserLastLoginAt(user: SuperAdminUser): string {
  return user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '—';
}

function getEmailVerified(user: SuperAdminUser): string {
  return user.verification && user.verification.status && user.verification.status.email ? 'Yes' : 'No';
}

function getSmsVerified(user: SuperAdminUser): string {
  return user.verification && user.verification.status && user.verification.status.sms ? 'Yes' : 'No';
}

function getPermissionsSummary(user: SuperAdminUser): string {
  const active = (user.permissions || []).filter(p => p.status === 'ACTIVE');
  if (active.length === 0) return '—';
  return active
    .map(p => {
      const school = p.school && p.school.name ? ` @ ${p.school.name}` : '';
      const misId = p.misId ? ` · MIS ID: ${p.misId}` : '';
      return `${p.preset || ''}${school}${misId}`;
    })
    .join(', ');
}

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'firstName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['firstName']
  },
  {
    text: 'Surname',
    field: 'lastName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['lastName']
  },
  {
    text: 'Email',
    field: 'email',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['email']
  },
  {
    text: 'Phone',
    field: 'phone',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['phone']
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: true,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getUserGender
  },
  {
    text: 'Birthday',
    field: 'birthday',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getUserBirthday
  },
  {
    text: 'Status',
    field: 'status',
    isSort: true,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getUserStatus
  },
  {
    text: 'Logged in',
    field: 'loggedIn',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getUserLoggedIn
  },
  {
    text: 'Last login',
    field: 'lastLoginAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getUserLastLoginAt
  },
  {
    text: 'Email verified',
    field: 'emailVerified',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getEmailVerified
  },
  {
    text: 'SMS verified',
    field: 'smsVerified',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getSmsVerified
  },
  {
    text: 'Permissions',
    field: 'permissions',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getPermissionsSummary
  },
  {
    text: 'Role filter',
    field: 'permissionPreset',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: () => ''
  },
  {
    text: 'Created',
    field: 'createdAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getUserCreatedAt
  },
  {
    text: 'Updated',
    field: 'updatedAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getUserUpdatedAt
  }
];

interface Props {
  history: History;
  location: Location;
}

interface State {
  items: SuperAdminUser[];
  currentPage: number;
  selectedItems: SuperAdminUser[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  errorMessage: string;
  isDetailsModalOpen: boolean;
  isDetailsLoading: boolean;
  detailsErrorMessage: string;
  selectedUserDetails?: SuperAdminUser;
}

function removeEmptyAnd(where: any): any {
  const whereCopy = { ...where };

  if (Array.isArray(whereCopy.$and) && whereCopy.$and.length === 0) {
    delete whereCopy.$and;
  }

  return whereCopy;
}

export class SuperAdminUsers extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      errorMessage: '',
      isDetailsModalOpen: false,
      isDetailsLoading: false,
      detailsErrorMessage: ''
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true, errorMessage: '' });
    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({ isLoading: true, errorMessage: '' });
      this.setItems(nextProps);
    }
  }

  setItems(props: Props = this.props) {
    const { history } = props;
    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':') as [string, TABLE_SORT_DIRECTION];
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';
    const filters = getFilters2(COLUMNS, search);
    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);
    const where = getSuperAdminUsersWhere(filters);
    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : 'lastName ASC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    return BPromise.all([getSuperAdminUsersCount(where), getSuperAdminUsers(serverQueryFilter)])
      .then(([countObj, items]) => {
        this.setState({
          currentPage: page,
          sortDirection,
          sortColumnsName,
          isShowFilter,
          isDataFiltered,
          filters,
          items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          errorMessage: ''
        });

        return true;
      })
      .catch(error => {
        console.error('Can not load superadmin users', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load users. Please check the session and try again.'
        });
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    const search: string[] = [];
    const { history } = this.props;
    const { sortDirection, sortColumnsName, filters } = this.state;

    search.push(`page=${currentPage}`);

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    history.push({ pathname: history.location.pathname, search: search.join('&') });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(column => column.field === filterField);
      const filterType = filter && filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const selectOptions = event.target.options;
        const value = [];

        for (let i = 0; i < selectOptions.length; i++) {
          if (selectOptions[i].selected) {
            value.push(selectOptions[i].value);
          }
        }

        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { history } = this.props;
    const { filters, sortDirection, sortColumnsName } = this.state;
    const search: string[] = [];

    search.push(`page=${FIRST_PAGE}`);

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    history.push({ pathname: history.location.pathname, search: search.join('&') });
  };

  onClearFilterClick = (): void => {
    const { history } = this.props;
    const { sortDirection, sortColumnsName } = this.state;
    const search: string[] = [];

    search.push(`page=${FIRST_PAGE}`);

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    history.push({ pathname: history.location.pathname, search: search.join('&') });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();
    this.setState({ isShowFilter: !this.state.isShowFilter });
  };

  onTableSortClick = (sortField: string): void => {
    const { history } = this.props;
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search: string[] = [];

    search.push(`page=${FIRST_PAGE}`);
    search.push(getSearchOrder(order, sortField));

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    history.push({ pathname: history.location.pathname, search: search.join('&') });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemIndex = selectedItems.findIndex(item => item.id === selectedItem.id);

    if (selectedItemIndex === -1) {
      this.setState({ selectedItems: [selectedItem], isSelectAllChecked: false });
    } else {
      this.setState({ selectedItems: [], isSelectAllChecked: false });
    }
  };

  onSelectAllClick = (): void => {
    const { filters } = this.state;
    const where = getSuperAdminUsersWhere(filters);

    this.setState({ isLoading: true, errorMessage: '' });

    getAllSuperAdminUsers(where)
      .then(items => {
        this.setState({
          selectedItems: items,
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all superadmin users', error);
        this.setState({ isLoading: false, errorMessage: 'Can not select all users.' });
      });
  };

  onSelectAllOnPageClick = (): void => {
    this.setState({ selectedItems: [...this.state.items], isSelectAllChecked: true });
  };

  onUnselectAllClick = (): void => {
    this.setState({ selectedItems: [], isSelectAllChecked: false });
  };

  onOpenUserClick = (): void => {
    const { selectedItems } = this.state;

    if (selectedItems.length !== 1) {
      return;
    }

    this.props.history.push(`/users/${selectedItems[0].id}`);
  };

  onAddUserClick = (): void => {
    this.props.history.push('/users/new');
  };

  onViewDetailsClick = (): void => {
    const { selectedItems } = this.state;

    if (selectedItems.length !== 1) {
      return;
    }

    const selectedUser = selectedItems[0];

    this.setState({
      isDetailsModalOpen: true,
      isDetailsLoading: true,
      detailsErrorMessage: '',
      selectedUserDetails: undefined
    });

    getSuperAdminUser(selectedUser.id)
      .then(user => {
        this.setState({
          selectedUserDetails: user,
          isDetailsLoading: false,
          detailsErrorMessage: ''
        });
      })
      .catch(error => {
        console.error('Can not load superadmin user details', error);
        this.setState({
          isDetailsLoading: false,
          detailsErrorMessage: 'Can not load user details.'
        });
      });
  };

  onCloseDetailsModal = (): void => {
    this.setState({
      isDetailsModalOpen: false,
      isDetailsLoading: false,
      detailsErrorMessage: '',
      selectedUserDetails: undefined
    });
  };

  renderUserDetailsModal() {
    const { isDetailsModalOpen, isDetailsLoading, detailsErrorMessage, selectedUserDetails } = this.state;

    if (!isDetailsModalOpen) {
      return null;
    }

    return (
      <div
        className="modal fade show SuperAdminUsersDetailsModal"
        style={{ display: 'block' }}
        tabIndex={-1}
        role="dialog"
      >
        <div className="modal-dialog modal-xl" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">User details</h5>
              <button type="button" className="close" onClick={this.onCloseDetailsModal}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              {isDetailsLoading && <Loader />}
              {detailsErrorMessage && <div className="alert alert-danger">{detailsErrorMessage}</div>}
              {!isDetailsLoading && selectedUserDetails && (
                <>
                  <div className="SuperAdminUsersDetailsGrid mb-3">
                    <div>
                      <strong>Name</strong>
                    </div>
                    <div>{getUserName(selectedUserDetails)}</div>
                    <div>
                      <strong>Email</strong>
                    </div>
                    <div>{selectedUserDetails.email || ''}</div>
                    <div>
                      <strong>Phone</strong>
                    </div>
                    <div>{selectedUserDetails.phone || ''}</div>
                    <div>
                      <strong>Status</strong>
                    </div>
                    <div>{selectedUserDetails.status || ''}</div>
                    <div>
                      <strong>Email verified</strong>
                    </div>
                    <div>{getEmailVerified(selectedUserDetails)}</div>
                    <div>
                      <strong>SMS verified</strong>
                    </div>
                    <div>{getSmsVerified(selectedUserDetails)}</div>
                    <div>
                      <strong>Created</strong>
                    </div>
                    <div>{getUserCreatedAt(selectedUserDetails)}</div>
                    <div>
                      <strong>Updated</strong>
                    </div>
                    <div>{getUserUpdatedAt(selectedUserDetails)}</div>
                  </div>

                  <h6>Permissions</h6>
                  <pre className="SuperAdminUsersJsonBlock">
                    {JSON.stringify(selectedUserDetails.permissions || [], null, 2)}
                  </pre>

                  <h6>Notification channels</h6>
                  <pre className="SuperAdminUsersJsonBlock">
                    {JSON.stringify(selectedUserDetails.notificationChannelsData || [], null, 2)}
                  </pre>

                  <h6>Full user</h6>
                  <pre className="SuperAdminUsersJsonBlock">{JSON.stringify(selectedUserDetails, null, 2)}</pre>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={this.onCloseDetailsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" />
      </div>
    );
  }

  render() {
    const {
      items,
      selectedItems,
      currentPage,
      total,
      isSelectAllChecked,
      isDataFiltered,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      filters,
      isLoading,
      errorMessage
    } = this.state;

    const actionItems: ActionItem[] = [
      {
        itemText: 'Add user',
        onItemClick: this.onAddUserClick,
        isActive: true
      },
      {
        itemText: 'Open user',
        onItemClick: this.onOpenUserClick,
        isActive: selectedItems.length === 1
      },
      {
        itemText: 'View details',
        onItemClick: this.onViewDetailsClick,
        isActive: selectedItems.length === 1
      }
    ];

    return (
      <div className="container-fluid mt-3 SuperAdminUsers">
        <h3>Users</h3>
        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {isLoading && <Loader />}

        {!isLoading && (
          <Grid2
            dataItems={items}
            filters={filters}
            options={USER_GRID_OPTIONS}
            currentPage={currentPage}
            total={total}
            isSelectAllChecked={isSelectAllChecked}
            isDataFiltered={isDataFiltered}
            sortDirection={sortDirection}
            sortColumnsName={sortColumnsName}
            isShowFilter={isShowFilter}
            dataItemsSelected={selectedItems}
            columns={COLUMNS}
            actionItems={actionItems}
            onItemClick={this.onItemClick}
            onSortClick={this.onTableSortClick}
            onApplyFilterClick={this.onApplyFilterClick}
            onClearFilterClick={this.onClearFilterClick}
            onTableFilterChange={this.onTableFilterChange}
            onTableFilterClick={this.onTableFilterClick}
            setCurrentPageParams={this.setCurrentPageParams}
            onSelectAllClick={this.onSelectAllClick}
            onSelectAllOnPageClick={this.onSelectAllOnPageClick}
            onUnselectAllClick={this.onUnselectAllClick}
          />
        )}

        {this.renderUserDetailsModal()}
      </div>
    );
  }
}
